import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { capitalizeWords } from "../utils/letters";
import PortalTopBar from "../components/portal/shared/PortalTopBar";
import PortalSidebarShell from "../components/portal/shared/PortalSidebarShell";
import ClassFooter from '../components/student/classroom/classFooter';

export default function BackofficeLayout({ footerData, isLoading: footerLoading }) {
	const navigate = useNavigate();
	const location = useLocation();
	const { logout, user } = useAuth();
	const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
	const [desktopSidebarHidden, setDesktopSidebarHidden] = useState(false);
	const [userMenuOpen, setUserMenuOpen] = useState(false);
	const userMenuRef = useRef(null);

	useEffect(() => {
		document.title = "Portal Admin | Somnus";
		return () => {
			document.title = "Somnus";
		};
	}, []);

	const adminDisplayName =
		user?.fullName ||
		[user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
		user?.username ||
		"Administrador";
	const normalizedAdminName = capitalizeWords(adminDisplayName);
	const adminInitial = normalizedAdminName.charAt(0).toUpperCase();

	useEffect(() => {
		setMobileSidebarOpen(false);
		setUserMenuOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!mobileSidebarOpen) return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [mobileSidebarOpen]);

	useEffect(() => {
		const onKeydown = (event) => {
			if (event.key === "Escape") setMobileSidebarOpen(false);
		};
		window.addEventListener("keydown", onKeydown);
		return () => window.removeEventListener("keydown", onKeydown);
	}, []);

	useEffect(() => {
		if (!userMenuOpen) return;

		const handleClickOutside = (event) => {
			if (!userMenuRef.current?.contains(event.target)) {
				setUserMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [userMenuOpen]);

	const toggleSidebar = () => {
		const isMobileViewport =
			typeof window !== "undefined" &&
			window.matchMedia("(max-width: 931px)").matches;

		if (isMobileViewport) {
			setMobileSidebarOpen((open) => !open);
			return;
		}

		setDesktopSidebarHidden((hidden) => !hidden);
	};

	const sidebarToggleLabel =
		mobileSidebarOpen || !desktopSidebarHidden
			? "Ocultar panel izquierdo"
			: "Mostrar panel izquierdo";
	const isSidebarVisible = mobileSidebarOpen || !desktopSidebarHidden;

	const handleLogout = async (e) => {
		e?.preventDefault?.();
		try {
			await logout();
		} finally {
			navigate("/login", { replace: true });
		}
	};

	return (
		<div className="student-portal-container teacher-portal-layout font-n">
			<PortalSidebarShell
				portalSubtitle="Panel Admin"
				userLabel="Administrador conectado"
				userName={normalizedAdminName}
				mobileSidebarOpen={mobileSidebarOpen}
				desktopSidebarHidden={desktopSidebarHidden}
				sidebarToggleLabel={sidebarToggleLabel}
				isSidebarVisible={isSidebarVisible}
				onToggleSidebar={toggleSidebar}
				onCloseSidebar={() => setMobileSidebarOpen(false)}
			>
				<NavLink
					to="/backoffice"
					end
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "dashboard")
					}
				>
					<i className="ri-dashboard-fill icon-part" />
					<span className="text-part">Panel</span>
				</NavLink>
				<Link
                    to="/"
                    className="nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 home"
                >
                    <i className="ri-home-2-fill icon-part" />
                    <span className="text-part">Inicio</span>
                </Link>

				<div
					className="text-uppercase text-muted mt-3 mb-1"
					style={{ fontSize: 11 }}
				>
					Datos
				</div>

				<NavLink
					to="/backoffice/companies"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "emp")
					}
				>
					<i className="ri-building-2-fill icon-part" />
					<span className="text-part">Organizaciones</span>
				</NavLink>

				<NavLink
					to="/backoffice/users"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "user")
					}
				>
					<i className="ri-user-3-fill icon-part" />
					<span className="text-part">Usuarios</span>
				</NavLink>

				<NavLink
					to="/backoffice/courses"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "course")
					}
				>
					<i className="ri-book-2-fill icon-part" />
					<span className="text-part">Cursos</span>
				</NavLink>

				<NavLink
					to="/backoffice/recorded-classes"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "record")
					}
				>
					<i className="ri-video-on-fill icon-part" />
					<span className="text-part">Clases grabadas</span>
				</NavLink>

				<NavLink
					to="/backoffice/final-exams"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "exam")
					}
				>
					<i className="ri-file-list-3-fill icon-part" />
					<span className="text-part">Exámenes finales</span>
				</NavLink>

				<NavLink
					to="/backoffice/talks"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "talks")
					}
				>
					<i className="ri-file-paper-2-fill icon-part" />
					<span className="text-part">Certificados</span>
				</NavLink>

				<div
					className="text-uppercase text-muted mt-4 mb-1"
					style={{ fontSize: 11 }}
				>
					Página pública
				</div>

				<NavLink
					to="/backoffice/news"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "news")
					}
				>
					<i className="ri-article-fill icon-part" />
					<span className="text-part">Noticias</span>
				</NavLink>

				<NavLink
					to="/backoffice/carousel"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "carousel")
					}
				>
					<i className="ri-slideshow-2-fill icon-part" />
					<span className="text-part">Carrusel</span>
				</NavLink>

				<NavLink
					to="/backoffice/web-config"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "text-contact")
					}
				>
					<i className="ri-settings-3-fill icon-part" />
					<span className="text-part">Textos y contacto</span>
				</NavLink>

				<div
					className="text-uppercase text-muted mt-4 mb-1"
					style={{ fontSize: 11 }}
				>
					Ayuda
				</div>
				<NavLink
					to="/backoffice/instructivo"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "instr")
					}
				>
					<i className="ri-book-open-fill icon-part" />
					<span className="text-part">Instructivo</span>
				</NavLink>

			</PortalSidebarShell>

			<main
				className="flex-grow-1 d-flex flex-column"
				style={{ minWidth: 0 }}
			>
				<PortalTopBar
					sidebarToggleLabel={sidebarToggleLabel}
					isSidebarVisible={isSidebarVisible}
					onToggleSidebar={toggleSidebar}
					userMenuOpen={userMenuOpen}
					userMenuRef={userMenuRef}
					onToggleUserMenu={() => setUserMenuOpen((open) => !open)}
					userAvatarColor="var(--light-blue)"
					userInitial={adminInitial}
					userDisplayName={normalizedAdminName}
					profileTo="/backoffice"
					onCloseUserMenu={() => setUserMenuOpen(false)}
					onLogout={handleLogout}
					menuAriaLabel="Opciones de cuenta administrador"
					showHeaderIcons={false}
					showProfileOption={false}
				/>

				<div className="p-4 pt-5 backoffice-tables flex-grow-1 overflow-auto font-n">
					<Outlet />
				</div>

				<ClassFooter footerData={footerData} isLoading={footerLoading} />
			</main>
		</div>
	);
}
